import { addGroup, deleteGroup, getGroup, updateGroup } from '@db/Groups';
import { getGroupsIn, getLargestGroups } from '@db/Groups/Get';
import { getUsersIn } from '@db/Users';
import { getErrorProps } from '@helpers/Errors';
import { sortByDisplayName } from '@helpers/Formatters';
import {
  AppQueryCache,
  AppQueryDomain,
  AppQueryKeySegment,
  createQueryIdListKeyPart,
  createQueryKey,
  useAppMutation,
  useAppQuery,
  useAppQueryCache,
} from '@helpers/query';
import type { Group, GroupFormData, Photo, UserInfo } from '@types';

const EMPTY_GROUPS: Group[] = [];
const EMPTY_USERS: UserInfo[] = [];

export type UseGroupQueryOptions = {
  /** Set false while route params are not ready. */
  enabled?: boolean;
  groupId?: string;
};

export type UseGroupAdminsQueryOptions = {
  /** Group whose admin users should be loaded. */
  group?: Group | null;
};

export type UseGroupsQueryOptions = {
  /** Set false while auth or parent data is not ready. */
  enabled?: boolean;
  /** Group ids loaded as a sorted group list. */
  groupIds: readonly string[];
};

export type UseLargestGroupsQueryOptions = {
  /** Set false while auth or parent context is not ready. */
  enabled?: boolean;
};

export type CreateGroupMutationVariables = {
  /** Group fields submitted by the create group form. */
  group: Pick<GroupFormData, 'adminIds' | 'description' | 'displayName'>;
  /** Optional local image selected for the new group. */
  photo?: Photo;
};

export type UpdateGroupMutationVariables = {
  /** Group fields submitted by the update group form. */
  group: Pick<GroupFormData, 'adminIds' | 'description' | 'displayName'>;
  /** Group being updated. */
  groupId: string;
  /** Optional local image selected for the group. */
  photo?: Photo;
};

export type DeleteGroupMutationVariables = {
  /** Group being deleted. */
  groupId: string;
};

export function useGroupQuery({ enabled = true, groupId }: UseGroupQueryOptions) {
  const isEnabled = enabled && Boolean(groupId);
  const query = useAppQuery<Group>({
    queryKey: createQueryKey(AppQueryDomain.GROUPS, AppQueryKeySegment.DETAIL, groupId ?? null),
    queryFn: () => getGroup(groupId ?? ''),
    enabled: isEnabled,
    retry: false,
  });

  return {
    error: query.isLoadingError,
    errorProps:
      query.error && query.isLoadingError
        ? getErrorProps(query.error, { onRetry: query.refetch })
        : undefined,
    group: isEnabled ? (query.data ?? null) : null,
    loading: isEnabled && query.isLoading,
  };
}

export function useGroupsQuery({ enabled = true, groupIds }: UseGroupsQueryOptions) {
  const normalizedGroupIds = createQueryIdListKeyPart(groupIds);
  const isEnabled = enabled && normalizedGroupIds.length > 0;
  const query = useAppQuery<Group[]>({
    queryKey: createQueryKey(AppQueryDomain.GROUPS, AppQueryKeySegment.LIST, normalizedGroupIds),
    queryFn: async () => sortByDisplayName(await getGroupsIn([...normalizedGroupIds])),
    enabled: isEnabled,
    retry: false,
  });

  return {
    groups: isEnabled ? (query.data ?? EMPTY_GROUPS) : EMPTY_GROUPS,
    loading: isEnabled && query.isLoading,
  };
}

export function useLargestGroupsQuery({ enabled = true }: UseLargestGroupsQueryOptions = {}) {
  const query = useAppQuery<Group[]>({
    queryKey: createQueryKey(AppQueryDomain.GROUPS, AppQueryKeySegment.LARGEST),
    queryFn: getLargestGroups,
    enabled,
    placeholderData: [],
    retry: false,
  });

  return {
    groups: enabled ? (query.data ?? EMPTY_GROUPS) : EMPTY_GROUPS,
    loading: enabled && query.isLoading,
  };
}

export function useGroupAdminsQuery({ group }: UseGroupAdminsQueryOptions) {
  const adminIds = createQueryIdListKeyPart(group?.adminIds);
  const isEnabled = Boolean(group?.groupId) && adminIds.length > 0;
  const query = useAppQuery<UserInfo[]>({
    queryKey: createQueryKey(
      AppQueryDomain.GROUPS,
      AppQueryKeySegment.ADMINS,
      group?.groupId ?? null,
      adminIds
    ),
    queryFn: () => getUsersIn([...adminIds]),
    enabled: isEnabled,
    retry: false,
  });

  return {
    admins: isEnabled ? (query.data ?? EMPTY_USERS) : EMPTY_USERS,
    error: query.isLoadingError,
    errorProps:
      query.error && query.isLoadingError
        ? getErrorProps(query.error, { onRetry: query.refetch })
        : undefined,
    loading: isEnabled && query.isLoading,
  };
}

export function useCreateGroupMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<string, Error, CreateGroupMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.GROUPS, AppQueryKeySegment.CREATE_MUTATION),
    mutationFn: ({ group, photo }) => addGroup(group, photo),
    onSuccess: async () => {
      await invalidateGroupMutationQueries(queryCache);
    },
  });
}

export function useUpdateGroupMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, UpdateGroupMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.GROUPS, AppQueryKeySegment.UPDATE_MUTATION),
    mutationFn: ({ group, groupId, photo }) => updateGroup(groupId, group, photo),
    onSuccess: async () => {
      await invalidateGroupMutationQueries(queryCache);
    },
  });
}

export function useDeleteGroupMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, DeleteGroupMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.GROUPS, AppQueryKeySegment.DELETE_MUTATION),
    mutationFn: ({ groupId }) => deleteGroup(groupId),
    onSuccess: async () => {
      await invalidateGroupMutationQueries(queryCache);
    },
  });
}

async function invalidateGroupMutationQueries(queryCache: AppQueryCache) {
  await Promise.all([
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.GROUPS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.USERS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.EXPLORER)),
  ]);
}
